import React, { useState, useRef, useEffect, useMemo } from "react";

import PropTypes from "prop-types";
import { useDisasterContext } from "../../Disasters/DisasterContext";
import ScreeningImmediateResponseSurvey from "./ScreeningImmediateResponseSurvey";
import ScreeningDamageAssessment from "./ScreeningDamageAssessment";
import ScreeningAssistanceLog from "./ScreeningAssistanceLog";
import ScreeningRecoveryPlanDisasters from "./ScreeningRecoveryPlan";
import ScreeningContacts from "./ScreeningContacts";
import citizenProfileService from "../../../service/citizen/CitizenProfile";
import DisasterMgtService from "../../../service/citizen/DisasterMgtService";
import ScreeningAccounting from "./ScreeningAccounting";
import ScreeningClosing from "./ScreeningClosing";

import { db } from "../../../firebase";
import {
  collection,
  query,
  where,
  getDocs,
  doc,
  getDoc,
} from "firebase/firestore";
import { getAuth } from "firebase/auth";
import { useParams } from "react-router-dom";
import ScreeningDA from "./ScreeningDA";
import { useDisasterObjContext } from "../../../context/DisasterObjContext";

const ScreeningDisaster = ({ className = "" }) => {
  const [activeTab, setActiveTab] = useState("Immediate Response Survey");
  const { activeDisasterTab, setActiveDisasterTab } = useDisasterContext();
  const [selectedDisaster, setSelectedDisaster] = useState("All");
  const [userRole, setUserRole] = useState("");

  const [setSelectedDisasters] = useState(["All"]);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [disasterOptions, setDisasterOptions] = useState([
    { id: "All", name: "All Disasters" },
  ]);
  const [activeDisasters, setActiveDisasters] = useState([]);
  const dropdownRef = useRef(null);

  const [showBlur, setShowBlur] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [activeView, setActiveView] = useState("survey");
  const [sidebarOpen, setSidebarOpen] = useState(true);

  // Add these states
  const [accessedCaseNumber, setAccessedCaseNumber] = useState(null);
  const [accessedDisasterId, setAccessedDisasterId] = useState(null);
  const [contractNo, setContractNo] = useState(null);
  const [addressData, setAddressData] = useState(null);
  const [birthDate, setBirthDate] = useState(null);
  const [mobileNumber, setMobileNumber] = useState(null);
  const [email, setEmail] = useState(null);
  const selectedUserId = sessionStorage.getItem("userId");
  const { disaster } = useDisasterObjContext();
  const [caseStatus, setCaseStatus] = useState("Unassigned");
  const selectedDisasters = useMemo(() => {
    if (disaster) {
      return [disaster?.id];
    }
    return [];
  }, [disaster]);
  const { subtab } = useParams();
  const [accessPermissions, setAccessPermissions] = useState({
    immediateResponse: false,
    damageAssessment: false,
    assistanceLog: false,
    lockbox: false,
  });
  useEffect(() => {
    const fetchUserRole = async () => {
      const auth = getAuth();
      if (auth.currentUser && selectedUserId) {
        const userDoc = await getDoc(doc(db, "users", selectedUserId));
        if (userDoc.exists()) {
          setUserRole(userDoc.data().role);
        }
      }
    };
    fetchUserRole();
  }, []);

  const [clientName, setClientName] = useState("");
  function generateUniqueId(length = 28) {
    const characters = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz";
    let result = "";
    for (let i = 0; i < length; i++) {
      result += characters.charAt(
        Math.floor(Math.random() * characters.length)
      );
    }
    return result;
  }
  // Add this new useEffect to fetch the name
  useEffect(() => {
    const fetchName = async () => {
      try {
        const auth = getAuth();
        if (!auth.currentUser?.uid || !selectedDisasters.length) return;

        // Get the accessed user ID from current user's document
        const userDoc = await getDoc(doc(db, "users", selectedUserId));
        const userData = userDoc.data();
        const accessedUserId = sessionStorage.getItem("accessedUserId");

        if (!accessedUserId) {
          console.log("No accessed user found");
          return;
        }

        // First fetch the default profile data
        const citizenData = await citizenProfileService.getCitizenProfile(
          "users",
          accessedUserId
        );

        if (citizenData) {
          // If we have selected disasters, try to fetch disaster-specific address
          if (selectedDisasters.length === 1) {
            const disasterId = selectedDisasters[0];
            const impactedAddressRef = doc(
              db,
              `users/${accessedUserId}/impacted-addresses/${disasterId}`
            );
            const impactedAddressDoc = await getDoc(impactedAddressRef);

            if (impactedAddressDoc.exists()) {
              // Use disaster-specific address data
              const addressData = impactedAddressDoc.data();
              console.log(addressData);
              setContractNo(addressData.mobile_number);
              setAddressData(addressData);
              setBirthDate(addressData.birth_date);
              setMobileNumber(addressData.mobile_number);
              setEmail(addressData.email);
              const fullName = `${addressData.first_name} ${addressData.last_name}`;
              setClientName(fullName);
            } else {
              // Use default profile data for new disaster entry
              const fullName = `${citizenData.first_name} ${citizenData.last_name}`;
              setClientName(fullName);
            }
          } else {
            // Use default profile data when no disaster is selected
            const fullName = `${citizenData.first_name} ${citizenData.last_name}`;
            setClientName(fullName);
          }
        } else {
          setClientName("");
        }
      } catch (error) {
        console.error("Error fetching name:", error);
        setClientName("");
      }
    };

    fetchName();
  }, [selectedDisasters]);

  useEffect(() => {
    // Only run this effect when selectedDisasters changes
    const handleImmediateNeeds = async () => {
      const accessedUserId = sessionStorage.getItem("accessedUserId");
      if (!accessedUserId || selectedDisasters.length === 0) return;

      try {
        // First, check if the user role is properly set
        if (!userRole) {
          console.log("User role not yet available, waiting...");
          return;
        }

        // Get existing records to check if any general records exist
        const existingRecords = await DisasterMgtService.fetchRecords(
          accessedUserId,
          "general"
        );

        // Check if ANY general record already exists for this disaster
        const hasGeneralRecord = existingRecords.some(
          (record) =>
            !record.is_deleted &&
            // Check if the record is associated with any of the selected disasters
            ((selectedDisasters.includes("All") &&
              record.associatedDisasters &&
              Array.isArray(record.associatedDisasters) &&
              record.associatedDisasters.some((d) =>
                activeDisasters.includes(d)
              )) ||
              (!selectedDisasters.includes("All") &&
                record.associatedDisasters &&
                Array.isArray(record.associatedDisasters) &&
                record.associatedDisasters.some((d) =>
                  selectedDisasters.includes(d)
                )))
        );

        // If any general record already exists, don't create a new one
        if (hasGeneralRecord) {
          console.log(
            "General record already exists for this disaster, no action needed"
          );
          return;
        }

        // Create a new Immediate Needs record if none exists
        const currentDate = new Date();
        const recordData = {
          generalID: generateUniqueId(),
          sub_category: "Immediate Needs",
          damage: "Immediate Needs",
          description: "Immediate Needs", // Default description
          editor_ID: selectedUserId, // Keep the editor as the center user
          editor_type: userRole, // This should now be correctly set
          is_deleted: false,
          last_edited: currentDate,
          date_declared: currentDate,
          date_entered: currentDate,
          estimate: Number(0),
          associatedDisasters: selectedDisasters.includes("All")
            ? activeDisasters
            : selectedDisasters,
        };

        // Create new record
        await DisasterMgtService.addRecord(
          accessedUserId, // Use accessedUserId for the record owner
          recordData,
          "general"
        );
        console.log("Created new immediate needs record");
      } catch (error) {
        console.error("Error handling immediate needs record:", error);
      }
    };

    // Only run this once when the component mounts or when selectedDisasters changes
    handleImmediateNeeds();
  }, [selectedDisasters, activeDisasters, userRole, selectedUserId]);
  // Modify the fetchDisasters useEffect
  // Modify the fetchDisasters useEffect in CaseManagementDisasters.js
  useEffect(() => {
    const fetchDisasters = async () => {
      try {
        setIsLoading(true);

        // First get the accessed case number and disaster ID
        const auth = getAuth();
        const userDoc = await getDoc(doc(db, "users", selectedUserId));
        const userData = userDoc.data();
        const accessedUserId = sessionStorage.getItem("accessedUserId");
        const caseNum = userData.accessed_case_number;

        setAccessedCaseNumber(caseNum);

        // Add this section to fetch case status
        const caseManagementQuery = query(
          collection(db, "case_management"),
          where("case_number", "==", caseNum),
          where("citizen_id", "==", accessedUserId)
        );

        const caseManagementSnapshot = await getDocs(caseManagementQuery);
        let caseStatus = "Unassigned"; // Default status

        if (!caseManagementSnapshot.empty) {
          const caseData = caseManagementSnapshot.docs[0].data();
          caseStatus = caseData.status;
        }

        // Add a new state to store the status
        setCaseStatus(caseStatus); // You'll need to add this state

        // Get the survey response for this case number
        const surveyQuery = query(
          collection(db, `users/${accessedUserId}/survey_responses`),
          where("case_number", "==", caseNum)
        );

        const surveySnapshot = await getDocs(surveyQuery);
        let associatedDisasters = [];

        if (!surveySnapshot.empty) {
          const surveyData = surveySnapshot.docs[0].data();
          if (surveyData.disasters) {
            associatedDisasters = surveyData.disasters.map((d) => ({
              id: d.disaster_id,
              name: d.disaster_name,
              status: "active",
            }));
          }
        }
        // Set the disasters options to only show associated disasters
        setDisasterOptions([...associatedDisasters]);
        setActiveDisasters(associatedDisasters);
        setSelectedDisasters(associatedDisasters.map((d) => d.id));

        setShowBlur(associatedDisasters.length === 0);
        setIsLoading(false);
      } catch (error) {
        console.error("Error fetching disasters:", error);
        setShowBlur(true);
        setIsLoading(false);
      }
    };

    fetchDisasters();
  }, []);

  // Remove or modify the All option from disaster selection
  const handleDisasterChange = (disaster) => {
    setSelectedDisasters((prev) => {
      const newSelection = prev.includes(disaster)
        ? prev.filter((d) => d !== disaster)
        : [...prev, disaster];
      return newSelection;
    });
  };

  const handleGoBack = () => {
    window.history.back();
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsDropdownOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const auth = getAuth();
  const [caseNumber, setCaseNumber] = useState("");
  const [femaNumber, setFemaNumber] = useState("");
  const [disasterNumber, setDisasterNumber] = useState("");

  useEffect(() => {
    const fetchCaseNumber = async () => {
      try {
        if (!selectedUserId) return;

        // Get the accessed user ID and case number from current user's document
        const userDoc = await getDoc(doc(db, "users", selectedUserId));
        const userData = userDoc.data();
        const accessedUserId = sessionStorage.getItem("accessedUserId");
        const accessedCaseNum = sessionStorage.getItem("accessedCaseNumber");

        if (!accessedUserId || !accessedCaseNum) {
          console.log("No accessed user or case number found");
          return;
        }

        // Get survey responses for the accessed user
        const surveyQuery = query(
          collection(db, `users/${accessedUserId}/survey_responses`),
          where("case_number", "==", accessedCaseNum)
        );

        const querySnapshot = await getDocs(surveyQuery);

        if (!querySnapshot.empty) {
          const surveyData = querySnapshot.docs[0].data();
          console.log("SURVEY DATA", surveyData);

          // Set the case number
          setCaseNumber(accessedCaseNum);
          setFemaNumber(surveyData.fema_number);
          setDisasterNumber(surveyData.disaster_number);

          // If there are disasters in the survey, update the selected disasters
          if (surveyData.disasters) {
            const surveyDisasterIds = surveyData.disasters.map(
              (d) => d.disaster_id
            );
            setSelectedDisasters(surveyDisasterIds);

            // Also update disaster options if needed
            const disasterDetails = surveyData.disasters.map((d) => ({
              id: d.disaster_id,
              name: d.disaster_name,
              status: "active",
            }));
            setDisasterOptions(disasterDetails);
          }

          console.log("Found case number:", accessedCaseNum);
          console.log("Associated disasters:", surveyData.disasters);
        }
      } catch (error) {
        console.error("Error fetching case number:", error);
        setCaseNumber("");
      }
    };

    fetchCaseNumber();
  }, []); // Remove dependencies since this should only run once on mount

  const tabs = [
    "Immediate Response Survey",
    "Damage Assessment",
    "Assistance Log",
    "Recovery Plan",
    "Contacts",
    "Journals",
    "Lockbox",
    "Case Notes",
  ];
  const handleTabClick = (tab) => {
    setActiveDisasterTab(tab);
  };

  const tabComponent = {
    irs: (
      <ScreeningImmediateResponseSurvey
        selectedDisasters={selectedDisasters}
        activeDisasters={disasterOptions}
        parentSetCaseNumber={setCaseNumber}
        caseStatus={caseStatus}
        accessPermissions={accessPermissions}
      />
    ),
    damageassessment: (
      <ScreeningDamageAssessment
        caseStatus={caseStatus}
        accessPermissions={accessPermissions}
      />
      // <CaseManagementDamageAssessment
      //   selectedDisasters={selectedDisasters}
      //   activeDisasters={activeDisasters}
      // />
    ),
    assistancelog: (
      <ScreeningAssistanceLog
        selectedDisasters={selectedDisasters}
        activeDisasters={activeDisasters}
        caseStatus={caseStatus}
        accessPermissions={accessPermissions}
      />
    ),
    recoverytracker: (
      <ScreeningRecoveryPlanDisasters
        selectedDisasters={selectedDisasters}
        activeDisasters={activeDisasters}
        caseNumber={caseNumber}
        femaNumber={femaNumber}
        disasterNumber={disasterNumber}
        caseStatus={caseStatus}
      />
    ),
    accounting: <ScreeningAccounting />,
    unmetneeds: <ScreeningClosing />,
  };

  const renderTabComponent = useMemo(() => {
    return tabComponent[subtab] || <></>;
  }, [subtab, tabComponent]);

  // if (showBlur) {
  //   return (
  //     <div className="absolute inset-0 z-50 flex items-center justify-center bg-opacity-10 backdrop-blur-md">
  //       <div className="flex flex-col items-center justify-center rounded-lg p-8 ">
  //         <div className="flex w-full flex-col items-center justify-center gap-2 space-x-4">
  //           <p className="text-[40px] font-bold text-[#348BFF]">
  //             This section will be activated when a disaster is declared
  //           </p>
  //           <button
  //             onClick={handleGoBack}
  //             className="mx-auto flex w-24 cursor-pointer items-center justify-center rounded-lg border border-blue-500 bg-white px-4 py-2 text-center text-blue-500"
  //           >
  //             Okay
  //           </button>
  //         </div>
  //       </div>
  //     </div>
  //   );
  // }

  return (
    <div className="flex-1">
      {/* Tab content wrapper */}
      <div className="relative">
        {" "}
        {/* Add relative positioning for overlay */}
        {caseStatus === "Closed" && (
          <div className="absolute inset-0 z-40 bg-gray-800 bg-opacity-10">
            <div className="border-b border-yellow-200 bg-yellow-50 p-3 text-center">
              <div className="flex items-center justify-center gap-2">
                <svg
                  className="h-5 w-5 text-yellow-600"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
                <span className="font-medium text-yellow-800">
                  This case is closed and is in view-only mode
                </span>
              </div>
            </div>
          </div>
        )}
        <div
          className={`mt-2 ${
            caseStatus === "Closed" ? "pointer-events-none opacity-75" : ""
          }`}
        >
          {renderTabComponent}
        </div>
      </div>
    </div>
  );
};

ScreeningImmediateResponseSurvey.propTypes = {
  className: PropTypes.string,
};

export default ScreeningDisaster;
